import type {
  WorksheetContent,
  WorksheetGrade,
} from "@bindery/shared/contracts/worksheet";
import { Button } from "@bindery/ui/components/ui/button";
import { Checkbox } from "@bindery/ui/components/ui/checkbox";
import { useId, useState, type FormEvent } from "react";

function WordGroup({
  title,
  description,
  words,
  marked,
  onChange,
}: {
  title: string;
  description: string;
  words: readonly string[];
  marked: ReadonlySet<string>;
  onChange: (next: ReadonlySet<string>) => void;
}) {
  const headingId = useId();
  if (words.length === 0) return null;
  const allMarked = words.every((word) => marked.has(word));
  const toggle = (word: string) => {
    const next = new Set(marked);
    if (!next.delete(word)) next.add(word);
    onChange(next);
  };
  return (
    <fieldset
      aria-labelledby={headingId}
      className="flex flex-col gap-3 border-t pt-4"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <h2 id={headingId} className="font-serif text-xl font-semibold">
            {title}
          </h2>
          <p className="text-muted-foreground text-sm">{description}</p>
        </div>
        <Button
          type="button"
          variant="link"
          size="sm"
          className="px-0"
          onClick={() => onChange(allMarked ? new Set() : new Set(words))}
        >
          {allMarked ? "Clear all" : "Mark all"}
        </Button>
      </div>
      <ul className="flex flex-wrap gap-2">
        {words.map((word) => (
          <li key={word}>
            <label className="bg-background has-[[data-state=checked]]:border-foreground has-[[data-state=checked]]:bg-foreground has-[[data-state=checked]]:text-background inline-flex cursor-pointer items-center gap-2 rounded-full border py-1.5 pr-4 pl-3 transition-colors select-none">
              <Checkbox
                checked={marked.has(word)}
                onCheckedChange={() => toggle(word)}
                name={word}
                className="data-[state=checked]:border-background data-[state=checked]:bg-background data-[state=checked]:text-foreground"
              />
              <span className="font-serif text-lg">{word}</span>
            </label>
          </li>
        ))}
      </ul>
    </fieldset>
  );
}

const NONE: ReadonlySet<string> = new Set();

export function GradeForm({
  content,
  onSubmit,
  busy = false,
}: {
  content: WorksheetContent;
  onSubmit: (grade: WorksheetGrade) => void;
  busy?: boolean;
}) {
  const [practiceFluent, setPracticeFluent] = useState(NONE);
  const [otherFluent, setOtherFluent] = useState(NONE);
  const [sightMissed, setSightMissed] = useState(NONE);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    onSubmit({
      practiceFluent: content.practiceWords.filter((w) =>
        practiceFluent.has(w),
      ),
      otherFluent: content.otherWords.filter((w) => otherFluent.has(w)),
      sightMissed: content.sightWords.filter((w) => sightMissed.has(w)),
    });
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-6">
      <WordGroup
        title="Practice words"
        description="Mark each boxed word the reader read on their own."
        words={content.practiceWords}
        marked={practiceFluent}
        onChange={setPracticeFluent}
      />
      <WordGroup
        title="Other words"
        description="Mark each of these the reader read on their own."
        words={content.otherWords}
        marked={otherFluent}
        onChange={setOtherFluent}
      />
      <WordGroup
        title="Sight words the reader missed"
        description="Mark only the sight words the reader could not read."
        words={content.sightWords}
        marked={sightMissed}
        onChange={setSightMissed}
      />
      <div className="flex items-center gap-4 border-t pt-4">
        <Button type="submit" disabled={busy}>
          {busy ? "Saving grades" : "Save grades"}
        </Button>
        <p className="text-muted-foreground text-sm">
          Unmarked words count as not yet read.
        </p>
      </div>
    </form>
  );
}
